import { authClient } from '@aimfora/auth/client'
import { m } from '@aimfora/i18n'
import { focusFirstInvalid } from '@aimfora/ui'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { z } from 'zod'
import { AuthCard, textLinkClass } from '~/components/auth-card'
import { FormError, SubmitButton, TextField } from '~/components/form-parts'
import { getPublicConfig, getSessionUser } from '~/functions/session.functions'
import { authErrorMessage, isEmailNotVerified } from '~/lib/auth-errors'
import { safeRedirect } from '~/lib/redirect'
import { loginSchema } from '~/lib/schemas'

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional(),
    email: z.string().optional(),
  }),
  beforeLoad: async ({ search }) => {
    if (await getSessionUser()) throw redirect({ href: safeRedirect(search.redirect) })
  },
  loader: () => getPublicConfig(),
  head: () => ({ meta: [{ title: `${m.auth_login_title()} – ${m.app_name()}` }] }),
  component: LoginPage,
})

function LoginPage() {
  const search = Route.useSearch()
  const router = useRouter()
  const formRef = useRef<HTMLFormElement>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { email: search.email ?? '', password: '' },
    validators: { onBlur: loginSchema(), onSubmit: loginSchema() },
    onSubmitInvalid: () => requestAnimationFrame(() => focusFirstInvalid(formRef.current)),
    onSubmit: async ({ value }) => {
      setServerError(null)
      const { error } = await authClient.signIn.email({
        email: value.email.trim(),
        password: value.password,
      })
      if (isEmailNotVerified(error)) {
        await router.navigate({
          to: '/verify-email',
          search: { email: value.email.trim(), redirect: search.redirect },
        })
        return
      }
      if (error) {
        setServerError(authErrorMessage(error))
        return
      }
      await router.invalidate()
      await router.navigate({ href: safeRedirect(search.redirect) })
    },
  })

  return (
    <AuthCard
      title={m.auth_login_title()}
      footer={
        <>
          {m.auth_new_teacher()}{' '}
          <Link className={textLinkClass} to="/signup" search={{ redirect: search.redirect }}>
            {m.auth_create_account_link()}
          </Link>
        </>
      }
    >
      <form
        ref={formRef}
        noValidate
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          void form.handleSubmit()
        }}
      >
        <FormError message={serverError} />
        <form.Field name="email">
          {(field) => (
            <TextField
              field={field}
              label={m.auth_email_label()}
              type="email"
              autoComplete="email"
              autoFocus
            />
          )}
        </form.Field>
        <form.Field name="password">
          {(field) => (
            <TextField
              field={field}
              label={m.auth_password_label()}
              type="password"
              autoComplete="current-password"
            />
          )}
        </form.Field>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(pending) => (
            <SubmitButton pending={pending} className="w-full">
              {m.auth_login_submit()}
            </SubmitButton>
          )}
        </form.Subscribe>
        <Link
          to="/reset-password"
          className={`inline-block text-ink-soft text-sm ${textLinkClass}`}
        >
          {m.auth_forgot_link()}
        </Link>
      </form>
    </AuthCard>
  )
}
